import React, {useEffect, useState, useContext} from "react";
import {Table, Pagination, Button} from "antd";
import {UserAuthenticationContext} from "../../Context/UserAuthenticationContext";
import {fetchSubscriptionsWithoutUsers} from "../../Api/VideoMetrics";
import * as XLSX from "xlsx";
import {saveAs} from "file-saver";

interface SubsNoUser {
    Email: string;
    SubscriptionDate: string;
    SubscriptionId: string;
    CustomerId: string;
    SubscriptionType: string;
}

const SubsNoUserReport: React.FC = () => {
    const [data, setData] = useState<SubsNoUser[]>([]);
    const [loading, setLoading] = useState<boolean>(true);
    const [error, setError] = useState<string | null>(null);
    const [currentPage, setCurrentPage] = useState(1);
    const [pageSize, setPageSize] = useState(10);
    const authCtx = useContext(UserAuthenticationContext);
    const access_token = authCtx.getAccessTokenSync() ?? "";

    useEffect(() => {
        const abortController = new AbortController();

        const loadSubscriptions = async () => {
            try {
                const result = await fetchSubscriptionsWithoutUsers(access_token, abortController);
                setData(result);
            } catch (err: any) {
                if (err.name !== "AbortError") {
                    console.error("❌ Failed to load data", err);
                    setError("Failed to load data");
                }
            } finally {
                setLoading(false);
            }
        };

        loadSubscriptions();
        return () => abortController.abort();
    }, [access_token]);

    const exportToExcel = () => {
        // Convert the data to a worksheet.
        const worksheet = XLSX.utils.json_to_sheet(data);
        // Create a new workbook and append the worksheet.
        const workbook = XLSX.utils.book_new();
        XLSX.utils.book_append_sheet(workbook, worksheet, "SubscriptionsWithoutUsers");
        // Write the workbook to an array buffer.
        const excelBuffer = XLSX.write(workbook, {bookType: "xlsx", type: "array"});
        // Create a Blob from the buffer and trigger a download.
        const blob = new Blob([excelBuffer], {type: "application/octet-stream"});
        saveAs(blob, "SubscriptionsWithoutUsers.xlsx");
    };

    const columns = [
        {title: "Email", dataIndex: "Email", key: "Email"},
        {
            title: "Subscription Date",
            dataIndex: "SubscriptionDate",
            key: "SubscriptionDate",
            render: (text: string) => new Date(text).toLocaleDateString(),
        },
        {title: "Subscription ID", dataIndex: "SubscriptionId", key: "SubscriptionId"},
        {title: "Customer ID", dataIndex: "CustomerId", key: "CustomerId"},
        {title: "Subscription Type", dataIndex: "SubscriptionType", key: "SubscriptionType"},
    ];

    if (loading) return <p>Loading...</p>;
    if (error) return <p style={{color: "red"}}>{error}</p>;

    return (
        <div style={{padding: "20px", backgroundColor: "#e6e0f8", borderRadius: "10px"}}>
            <div
                style={{
                    display: "flex",
                    justifyContent: "space-between",
                    alignItems: "center",
                    marginBottom: "10px",
                }}
            >
                <div>
                    <h2 style={{margin: 0, color: "#333", display: "inline-block", marginRight: "10px"}}>
                        Subscriptions Without Users
                    </h2>
                    <span style={{color: "#333", fontSize: "14px"}}>
            Total: {data.length}
          </span>
                </div>
                <div style={{display: "flex", alignItems: "center"}}>
                    <Button type="primary" onClick={exportToExcel} style={{marginRight: "20px"}}>
                        Export to Excel
                    </Button>
                    <Pagination
                        current={currentPage}
                        pageSize={pageSize}
                        total={data.length}
                        onChange={(page, size) => {
                            setCurrentPage(page);
                            setPageSize(size);
                        }}
                        showSizeChanger
                    />
                </div>
            </div>
            <Table
                dataSource={data.slice((currentPage - 1) * pageSize, currentPage * pageSize)}
                columns={columns}
                pagination={false}
                rowKey="SubscriptionId"
                style={{
                    backgroundColor: "#ffffff",
                    borderRadius: "8px",
                    padding: "10px",
                    boxShadow: "0 2px 8px rgba(0,0,0,0.1)",
                }}
            />
        </div>
    );
};

export default SubsNoUserReport;
